"use client";

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, ClipboardList, LogOut, User as UserIcon } from "lucide-react";
import logo from "../assets/logocopacabana.png";

// Mapeos de roles según tu tabla dbo.user_roles
const ROLE_LABELS_BY_ID = {
  1: "Solicitante",
  2: "Planeación",
  3: "Hacienda",
  4: "Jurídica",
  5: "Alcalde",
  6: "Administrador",
  7: "Presupuesto",
  8: "Almacén",
  9: "Validador (Inexistencia)",
  10: "Secretario (Inexistencia)",
  11: "Alcalde (Inexistencia)",
};

const ROLE_LABELS_BY_NAME = {
  solicitante: "Solicitante",
  planeacion: "Planeación",
  hacienda: "Hacienda",
  juridica: "Jurídica",
  alcalde: "Alcalde",
  administrador: "Administrador",
  presupuesto: "Presupuesto",
  almacen: "Almacén",
  validador: "Validador (Inexistencia)",
  firma_secretario: "Secretario (Inexistencia)",
  firma_alcalde: "Alcalde (Inexistencia)",
};

function getRoleId(user) {
  if (!user) return null;

  const raw =
    user.role_id ??
    user.rolId ??
    user.roleId ??
    null;

  if (raw === null || raw === undefined) return null;

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function getRoleKeyFromUser(user) {
  if (!user) return null;

  const raw =
    user.role ??
    user.roleName ??
    user.rol ??
    null;

  if (raw === null || raw === undefined) return null;

  const str = String(raw).trim();
  if (/^\d+$/.test(str)) return null; // si viene "9" como string, mejor usar role_id

  return str.toLowerCase();
}

function getLandingPathByRole(user) {
  const roleId = getRoleId(user);
  const roleKey = getRoleKeyFromUser(user);

  // 1) Prioridad: role_id (más confiable)
  if (roleId !== null) {
    // Almacén: solo bandeja
    if (roleId === 8) return "/almacen/solicitudes";

    // Inexistencia: solo bandeja
    if (roleId === 9 || roleId === 10 || roleId === 11) return "/inexistencia/solicitudes";

    // Validadores tradicionales: dashboard directo
    if ([2, 3, 4, 5, 6, 7].includes(roleId)) return "/dashboard";

    // Solicitante u otros: se quedan en menú
    return null;
  }

  // 2) Fallback: si no hay role_id, intentamos por nombre
  if (roleKey) {
    if (roleKey === "almacen") return "/almacen/solicitudes";
    if (roleKey === "validador" || roleKey === "firma_secretario" || roleKey === "firma_alcalde") {
      return "/inexistencia/solicitudes";
    }
    if (["planeacion", "hacienda", "juridica", "alcalde", "administrador", "presupuesto"].includes(roleKey)) {
      return "/dashboard";
    }
  }

  return null;
}

function getNiceRoleLabel(user) {
  if (!user) return "";

  const id = getRoleId(user);
  const key = getRoleKeyFromUser(user);

  // si existe rol por nombre (no numérico), úsalo
  if (key) {
    if (ROLE_LABELS_BY_NAME[key]) return ROLE_LABELS_BY_NAME[key];
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  // si existe id, úsalo
  if (id !== null) {
    if (ROLE_LABELS_BY_ID[id]) return ROLE_LABELS_BY_ID[id];
    return `Rol ${id}`;
  }

  return "";
}

export default function Menu() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState("");
  const [roleName, setRoleName] = useState("");

  useEffect(() => {
    const load = () => {
      try {
        const storedToken = localStorage.getItem("auth_token");
        const storedUser = localStorage.getItem("auth_user");

        // Si no hay sesión, mandamos al login
        if (!storedToken || !storedUser) {
          navigate("/login", { replace: true });
          return;
        }

        const user = JSON.parse(storedUser);
        setUserEmail(user.email || "");

        const niceRole = getNiceRoleLabel(user);
        setRoleName(niceRole);

        // ✅ Redirección estable por role_id / role (no por label)
        const landing = getLandingPathByRole(user);
        if (landing) {
          navigate(landing, { replace: true });
          return;
        }
      } catch (e) {
        console.error("Error leyendo sesión:", e);
        navigate("/login", { replace: true });
      }
    };

    load();
  }, [navigate]);

  const handleLogout = () => {
    try {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_user");
      sessionStorage.clear();
    } catch (e) {
      console.error("Error al limpiar almacenamiento:", e);
    } finally {
      navigate("/login", { replace: true });
    }
  };

  // estilos
  const styles = {
    page: {
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      background: "#f7f8fa",
      padding: "24px",
    },
    hero: {
      width: "100%",
      maxWidth: 1100,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 16,
      marginBottom: 24,
    },
    title: { fontSize: 28, fontWeight: 800, color: "#1e3a8a", textAlign: "center" },
    userRow: {
      display: "flex",
      gap: 12,
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "wrap",
      marginTop: 6,
    },
    userChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      background: "#eef2ff",
      color: "#1e3a8a",
      borderRadius: 999,
      padding: "10px 14px",
      fontSize: 14,
      boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    },
    logoutBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      background: "#ef4444",
      color: "white",
      border: "none",
      borderRadius: 999,
      padding: "10px 16px",
      cursor: "pointer",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    },
    gridWrap: { width: "100%", display: "flex", justifyContent: "center" },
    grid: {
      width: "100%",
      maxWidth: 1100,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
      gap: 20,
    },
    card: {
      display: "block",
      textDecoration: "none",
      color: "inherit",
      borderRadius: 16,
      background: "white",
      border: "1px solid #e5e7eb",
      padding: 24,
      boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
      transition: "transform 120ms ease, box-shadow 120ms ease",
    },
    cardHover: {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
    },
    cardHeader: { display: "flex", alignItems: "center", gap: 16 },
    iconWrap: {
      display: "grid",
      placeItems: "center",
      width: 52,
      height: 52,
      borderRadius: 12,
      background: "#f3f4f6",
    },
    cardTitle: { fontSize: 20, fontWeight: 700, color: "#111827", margin: 0 },
    cardDesc: { fontSize: 14, color: "#6b7280", margin: "6px 0 0 0" },
  };

  const withHover = (base) => ({
    style: base,
    onMouseEnter: (e) => Object.assign(e.currentTarget.style, styles.cardHover),
    onMouseLeave: (e) => Object.assign(e.currentTarget.style, base),
  });

  // Para roles como solicitante (u otros no redirigidos)
  return (
    <div style={styles.page}>
      {/* HERO centrado */}
      <section style={styles.hero}>
        <img src={logo} alt="Logo" style={{ height: 180, width: "auto" }} />
        <h1 style={styles.title}>Necesidades De Contratación</h1>

        <div style={styles.userRow}>
          <span style={styles.userChip}>
            <UserIcon size={16} />
            {userEmail || "usuario@correo.com"}
            {roleName ? ` (${roleName})` : ""}
          </span>

          <button type="button" onClick={handleLogout} style={styles.logoutBtn}>
            <LogOut size={18} />
            Cerrar sesión
          </button>
        </div>
      </section>

      {/* Tarjetas (para roles NO redirigidos) */}
      <div style={styles.gridWrap}>
        <div style={styles.grid}>
          <Link to="/dashboard" {...withHover(styles.card)} aria-label="Ir a Necesidades">
            <div style={styles.cardHeader}>
              <div style={styles.iconWrap}>
                <FileText size={22} />
              </div>
              <div>
                <h2 style={styles.cardTitle}>Necesidades</h2>
                <p style={styles.cardDesc}>Crear, revisar y gestionar necesidades/contratos.</p>
              </div>
            </div>
          </Link>

          <Link
            to="/solicitud-adquisiciones"
            {...withHover(styles.card)}
            aria-label="Ir a Solicitud Adquisiciones"
          >
            <div style={styles.cardHeader}>
              <div style={styles.iconWrap}>
                <ClipboardList size={22} />
              </div>
              <div>
                <h2 style={styles.cardTitle}>Solicitud Adquisiciones</h2>
                <p style={styles.cardDesc}>Creación y gestión de solicitudes de adquisiciones.</p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
